import {Link} from '@primer/react'

import type {UpdateColumnValueAction} from '../../api/columns/contracts/domain'
import {MemexColumnDataType, SystemColumnId} from '../../api/columns/contracts/memex-column'
import type {ExtendedRepository, Milestone, Repository} from '../../api/common-contracts'
import type {TrackedByItem} from '../../api/issues-graph/contracts'
import {apiGetSuggestedMilestones} from '../../api/memex-items/api-get-suggested-milestones'
import {ItemType} from '../../api/memex-items/item-type'
import {DragAndDropStatus} from '../../api/stats/contracts'
import type {AddToastProps} from '../../components/toasts/use-toasts'
import {not_typesafe_nonNullAssertion} from '../../helpers/non-null-assertion'
import type {ConfirmOptions} from '../../hooks/use-alert'
import type {MemexItemModel} from '../../models/memex-item-model'
import {ItemUpdateValidationResources, TrackedByResources} from '../../strings'

export const RegroupValidationUIAction = {
  ErrorToast: 'error-toast',
  ConfirmDialog: 'confirm-dialog',
  Alert: 'alert',
} as const
type RegroupValidationUIAction = ObjectValues<typeof RegroupValidationUIAction>

export const RegroupValidationStatus = {
  Success: 'success',
  Failure: 'failure',
  Pending: 'pending',
} as const
type RegroupValidationStatus = ObjectValues<typeof RegroupValidationStatus>

interface BaseRegroupValidationResult {
  status: RegroupValidationStatus
  stats?: {
    status: DragAndDropStatus
    itemId: number
    columnId: SystemColumnId
  }
  updateColumnActionOverride?: UpdateColumnValueAction
}

interface RegroupValidationSuccessResult extends BaseRegroupValidationResult {
  status: typeof RegroupValidationStatus.Success
}

// Not successful - display alert dialog
interface RegroupValidationAlertResult extends BaseRegroupValidationResult {
  status: typeof RegroupValidationStatus.Failure
  action: typeof RegroupValidationUIAction.Alert
  alertOptions: ConfirmOptions
}
// Not successful - display toast
interface RegroupValidationErrorResult extends BaseRegroupValidationResult {
  status: typeof RegroupValidationStatus.Failure
  action: typeof RegroupValidationUIAction.ErrorToast
  toastOptions: AddToastProps
}

// Requires confirmation - display confirm dialog
interface RegroupValidationConfirmResult extends BaseRegroupValidationResult {
  status: typeof RegroupValidationStatus.Pending
  action: typeof RegroupValidationUIAction.ConfirmDialog
  confirmOptions: ConfirmOptions
}

type ColumnUpdateResult =
  | RegroupValidationSuccessResult
  | RegroupValidationAlertResult
  | RegroupValidationConfirmResult
  | RegroupValidationErrorResult

// Milestone
const createMilestoneTransferDialogContent = (milestone: Milestone, repository: Repository): React.ReactNode => {
  return (
    <>
      Please create the <code>{milestone.title}</code> milestone in the{' '}
      <a href={`${repository.url}/milestones`}>{repository.nameWithOwner}</a> repository before adding to this milestone
      group.
    </>
  )
}

const getMilestoneValidationResult = async (
  item: MemexItemModel,
  milestone?: Milestone,
): Promise<ColumnUpdateResult> => {
  if (!milestone) return {status: RegroupValidationStatus.Success}

  const baseStatsPayload = {
    itemId: item.id,
    columnId: SystemColumnId.Milestone,
  }

  if (item.contentType === ItemType.DraftIssue) {
    // drafts cannot be dropped into an existing milestone as the
    // backend does not support these changes
    return {
      status: RegroupValidationStatus.Failure,
      action: RegroupValidationUIAction.Alert,
      alertOptions: ItemUpdateValidationResources.Milestone.Alerts.NeedsConversion,
      stats: {...baseStatsPayload, status: DragAndDropStatus.FAILURE},
    }
  }

  if (item.columns.Milestone && item.columns.Milestone.id === milestone.id) {
    // if the milestone of the row being moved matches the milestone of the
    // drop zone we can proceed - the user is likely reordering rows in the
    // same group
    return {status: RegroupValidationStatus.Success}
  }

  const response = await apiGetSuggestedMilestones({memexProjectItemId: item.id})
  const targetMilestoneForRepository = response.suggestions.find(m => m.title === milestone.title)

  if (targetMilestoneForRepository) {
    return {
      status: RegroupValidationStatus.Success,
      stats: {...baseStatsPayload, status: DragAndDropStatus.SUCCESS},
      // The same milestone name can exist in multiple repos, so this ensures that
      // we're using the correct one.
      updateColumnActionOverride: {
        dataType: MemexColumnDataType.Milestone,
        value: targetMilestoneForRepository,
      },
    }
  }

  return {
    status: RegroupValidationStatus.Failure,
    action: RegroupValidationUIAction.Alert,
    alertOptions: {
      ...ItemUpdateValidationResources.Milestone.Alerts.NeedsTransfer,
      content: createMilestoneTransferDialogContent(milestone, not_typesafe_nonNullAssertion(item.columns.Repository)),
    },
    stats: {...baseStatsPayload, status: DragAndDropStatus.FAILURE},
  }
}

// Repository
const createIssueTransferDialogContent = (link: string): React.ReactNode => {
  return (
    <div>
      You can transfer this issue to a different repository from the&nbsp;
      <Link target="_blank" href={link}>
        issue page
      </Link>
      .
    </div>
  )
}

const getRepositoryValidationResult = (item: MemexItemModel, repository?: ExtendedRepository): ColumnUpdateResult => {
  if (!repository) return {status: RegroupValidationStatus.Success}

  const statsFailurePayload = {
    itemId: item.id,
    columnId: SystemColumnId.Repository,
    status: DragAndDropStatus.FAILURE,
  }

  if (item.contentType === ItemType.DraftIssue) {
    return {
      status: RegroupValidationStatus.Failure,
      action: RegroupValidationUIAction.Alert,
      alertOptions: ItemUpdateValidationResources.Repository.Alerts.NeedsConversion,
      stats: statsFailurePayload,
    }
  }

  if (item.contentType === ItemType.PullRequest) {
    return {
      status: RegroupValidationStatus.Failure,
      action: RegroupValidationUIAction.Alert,
      alertOptions: ItemUpdateValidationResources.Repository.Alerts.CannotTransferPulls,
      stats: statsFailurePayload,
    }
  }

  if (
    item.contentType === ItemType.Issue &&
    item.columns.Repository?.nameWithOwner !== repository.nameWithOwner &&
    item.content &&
    'url' in item.content
  ) {
    return {
      status: RegroupValidationStatus.Failure,
      action: RegroupValidationUIAction.Alert,
      alertOptions: {
        ...ItemUpdateValidationResources.Repository.Alerts.IssueNeedsTransfer,
        content: createIssueTransferDialogContent(item.content.url),
      },
      stats: statsFailurePayload,
    }
  }

  return {status: RegroupValidationStatus.Success}
}

// Tracked by
const getTrackedByValidationResult = (
  item: MemexItemModel,
  trackedByItems: Array<TrackedByItem>,
): ColumnUpdateResult => {
  if (
    // only display a warning if the user is trying to add a tracked by value, as opposed to sort items _in_ "No Tracked by"
    trackedByItems.length > 0 &&
    (item.contentType === ItemType.DraftIssue || item.contentType === ItemType.PullRequest)
  ) {
    return {
      status: RegroupValidationStatus.Failure,
      action: RegroupValidationUIAction.ErrorToast,
      toastOptions: TrackedByResources.Toasts.DraftAndPullsNotSupported,
      stats: {status: DragAndDropStatus.FAILURE, itemId: item.id, columnId: SystemColumnId.TrackedBy},
    }
  }

  if (
    // If the item is tracked by 2 parent issues
    (item.columns[SystemColumnId.TrackedBy]?.length ?? 0) > 1 &&
    // ...and they want to move it to "No Tracked by"
    trackedByItems.length === 0
  ) {
    // we need to confirm with the user that they understand that the item will be removed from both parent issues
    return {
      status: RegroupValidationStatus.Pending,
      action: RegroupValidationUIAction.ConfirmDialog,
      confirmOptions: TrackedByResources.moveMultipleTrackedByNoTrackedWarning,
    }
  }

  return {status: RegroupValidationStatus.Success}
}

export async function getUpdateGroupValidation(
  item: MemexItemModel,
  updateColumnAction: UpdateColumnValueAction,
): Promise<ColumnUpdateResult> {
  switch (updateColumnAction.dataType) {
    case MemexColumnDataType.Repository:
      return getRepositoryValidationResult(item, updateColumnAction.value)
    case MemexColumnDataType.Milestone:
      return await getMilestoneValidationResult(item, updateColumnAction.value)
    case MemexColumnDataType.TrackedBy:
      return getTrackedByValidationResult(item, updateColumnAction.value)
  }
  return {status: RegroupValidationStatus.Success}
}
